/**
 * Usage meter state and the `/usage` API client.
 *
 * Usage is only ever a percentage here: the backend never sends dollars, tokens or request counts, and the UI
 * never shows them. The composer ring shows at every usage level (the backend's 50% `visible` flag is no longer used).
 *
 * Buying usage and automatic recharge are web-only; the API refuses them for the iOS / Android apps and
 * `can_purchase` is false there, so the details sheet only explains where to add usage.
 */
import { computed, readonly, ref } from 'vue'
import { getNativePlatform } from '@/utils/platform'

export interface UsageStatus {
    /** Backend's 50% rule. The app no longer gates the composer ring on it. */
    visible: boolean
    /** 0–100, whole numbers. */
    percent: number
    exhausted: boolean
    resets_at: string | null
}

export interface UsageDetails extends UsageStatus {
    monthly: { percent: number }
    credits: { has_credits: boolean; percent_used: number }
    auto_recharge: { enabled: boolean; pack: number | null; disabled_reason: string | null }
    /** What each pack costs in whole dollars. Never how much usage it adds. */
    packs: number[]
    can_manage: boolean
    can_purchase: boolean
}

export class UsageApiError extends Error {
    constructor(message: string, public status: number, public code?: string) {
        super(message)
    }
}

export interface UsageTransport {
    request<T>(method: 'GET' | 'POST' | 'PUT', path: string, body?: unknown): Promise<T>
}

/** The app's authenticated axios client (lazily imported so tests never load Supabase). */
export const httpUsageTransport: UsageTransport = {
    async request(method, path, body) {
        const { default: client } = await import('@/api/auth-http-client')
        const res: any = await client.request({
            method,
            url: `/usage${path}`,
            data: body,
            headers: { 'X-Lighthouse-Platform': getNativePlatform() },
        })
        const status = res?.status ?? 500
        // status 0: no response while offline (auth-http-client).
        if (status >= 400 || status === 0) {
            const err = res?.data?.error || {}
            throw new UsageApiError(err.message || `Request failed (${status})`, status, err.code)
        }
        return res.data
    },
}

export function createUsageApi(t: UsageTransport = httpUsageTransport) {
    return {
        status: () => t.request<UsageStatus>('GET', '/status'),
        details: () => t.request<UsageDetails>('GET', '/details'),
        checkout: (pack: number, redirectPath: string) =>
            t.request<{ url: string }>('POST', '/credits/checkout', { pack, redirect_path: redirectPath, cancel_path: redirectPath }),
        confirm: (sessionId: string) =>
            t.request<{ paid: boolean; granted: boolean }>('POST', '/credits/confirm', { session_id: sessionId }),
        setAutoRecharge: (enabled: boolean, pack: number | null) =>
            t.request<UsageDetails>('PUT', '/auto-recharge', { enabled, pack }),
    }
}

export type UsageApi = ReturnType<typeof createUsageApi>

let _api: UsageApi | null = null
export function usageApi(): UsageApi {
    if (!_api) _api = createUsageApi()
    return _api
}
/** Tests swap the transport. */
export function setUsageApi(api: UsageApi | null): void {
    _api = api
}

// ── shared state: one meter value for every composer on the page ─────────────────────────────────────
const status = ref<UsageStatus | null>(null)
const details = ref<UsageDetails | null>(null)
let inflight: Promise<void> | null = null
let lastRefresh = 0
const THROTTLE_MS = 1500

/** Re-read the meter. Cheap and safe to call often (after a run, on focus); concurrent calls share one request. */
export function refreshUsage(opts: { force?: boolean } = {}): Promise<void> {
    if (inflight) return inflight
    if (!opts.force && Date.now() - lastRefresh < THROTTLE_MS) return Promise.resolve()
    inflight = (async () => {
        try {
            status.value = await usageApi().status()
            lastRefresh = Date.now()
            if (details.value) Object.assign(details.value, status.value)
        } catch {
            /* the meter is a convenience: a failed read leaves the last value in place */
        } finally {
            inflight = null
        }
    })()
    return inflight
}

/** Take a details payload from the server (the meter value rides along in it). */
export function applyUsageDetails(next: UsageDetails): void {
    details.value = next
    status.value = { visible: next.visible, percent: next.percent, exhausted: next.exhausted, resets_at: next.resets_at }
    lastRefresh = Date.now()
}

export async function loadUsageDetails(): Promise<UsageDetails | null> {
    try {
        applyUsageDetails(await usageApi().details())
    } catch {
        /* keep whatever we had */
    }
    return details.value
}

export function _resetUsage(): void {
    status.value = null
    details.value = null
    inflight = null
    lastRefresh = 0
    handledReturns.clear()
}

const handledReturns = new Set<string>()

/**
 * The page the customer returns to after paying: `?usage_credits=success&session_id=cs_…`. Confirms the purchase
 * (idempotent: the webhook may have granted it already) and refreshes. Returns true the first time a given
 * session is handled, so only one meter on the page reacts.
 */
export async function confirmCheckoutReturn(query: Record<string, unknown>): Promise<boolean> {
    const sid = query.session_id
    if (query.usage_credits !== 'success' || typeof sid !== 'string' || handledReturns.has(sid)) return false
    handledReturns.add(sid)
    try {
        await usageApi().confirm(sid)
    } catch {
        /* the webhook will grant it; the meter catches up on the next refresh */
    }
    await loadUsageDetails()
    return true
}

export function useUsage() {
    return {
        status: readonly(status),
        details: readonly(details),
        /** The composer ring always shows once the status has loaded, at any usage level. The one exception is an
         *  org with no limit (the backend sends no reset date for it): there is nothing to meter. The backend's
         *  `visible` (the 50% rule) is no longer used here. */
        visible: computed(() => !!status.value && status.value.resets_at !== null),
        percent: computed(() => status.value?.percent ?? 0),
        exhausted: computed(() => !!status.value?.exhausted),
        resetsAt: computed(() => status.value?.resets_at ?? null),
        refresh: refreshUsage,
        loadDetails: loadUsageDetails,
    }
}

/** Leaves the app for Stripe Checkout. A seam so tests don't navigate. */
export const nav = {
    assign: (url: string): void => window.location.assign(url),
}

// ── copy ────────────────────────────────────────────────────────────────────────────────────────────
export function formatResetDate(iso: string | null): string {
    if (!iso) return ''
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * The limit message where nothing may be sold (companion mode, decision D-4): no "add more", no plans.
 * `resetsLabel` is formatResetDate's output.
 */
export function usageLimitReachedMessage(resetsLabel: string): string {
    return resetsLabel
        ? `You’ve reached this month’s AI usage. It resets on ${resetsLabel}.`
        : 'You’ve reached this month’s AI usage.'
}

const RECHARGE_PAUSED: Record<string, string> = {
    payment_failed: 'Automatic recharge was turned off because the last payment didn’t go through.',
    no_payment_method: 'Automatic recharge was turned off because there’s no card on file.',
    action_required: 'Automatic recharge was turned off because your bank needs you to confirm the payment.',
}

export function rechargePausedMessage(reason: string | null | undefined): string {
    return reason ? (RECHARGE_PAUSED[reason] ?? 'Automatic recharge was turned off.') : ''
}
